import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCandidates, setActiveMonth } from "../../Redux/candidatesSlice";
import MonthPicker from "./MonthPicker";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Work,
  LocationOn,
  OpenInNew,
  Article,
  Close,
  Groups,
  CalendarToday,
  Fingerprint,
  Badge,
  WorkHistory,
  Refresh,
  Search,
  FilterList,
  ContentCopy,
} from "@mui/icons-material";

// Format Date → "Feb-2026"
const toMonthYear = (date) =>
  date.toLocaleDateString("en-US", { month: "short", year: "numeric" }).replace(" ", "-");

// Shift month by delta
const shiftMonth = (date, delta) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + delta);
  d.setDate(1);
  return d;
};

const DEPT_COLORS = {
  Sales:       { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  Marketing:   { bg: "#fdf4ff", color: "#9333ea", border: "#e9d5ff" },
  Engineering: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  Design:      { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  HR:          { bg: "#fef9c3", color: "#ca8a04", border: "#fde68a" },
  Finance:     { bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
  Operations:  { bg: "#faf5ff", color: "#7c3aed", border: "#ddd6fe" },
  Default:     { bg: "#f8fafc", color: "#475569", border: "#e2e8f0" },
};

const deptStyle = (dept) => DEPT_COLORS[dept] || DEPT_COLORS.Default;

const MODE_CHIP = {
  Remote:    { bg: "#f0fdf4", color: "#16a34a" },
  Hybrid:    { bg: "#eff6ff", color: "#2563eb" },
  "On-site": { bg: "#fff7ed", color: "#ea580c" },
};
const modeStyle = (mode) => MODE_CHIP[mode] || { bg: "#f8fafc", color: "#475569" };

const STATUS_STYLES = {
  "Applied":              { bg: "#f1f5f9", color: "#475569" },
  "Under Review":         { bg: "#eff6ff", color: "#2563eb" },
  "Shortlisted":          { bg: "#fdf4ff", color: "#9333ea" },
  "Interview Scheduled":  { bg: "#fef3c7", color: "#d97706" },
  "Offer Extended":       { bg: "#ecfdf5", color: "#059669" },
  "Hired":                { bg: "#dcfce7", color: "#16a34a" },
  "Rejected":             { bg: "#fef2f2", color: "#dc2626" },
  "On Hold":              { bg: "#fff7ed", color: "#ea580c" },
};
const statusStyle = (s) => STATUS_STYLES[s] || { bg: "#f1f5f9", color: "#475569" };

export const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state — candidates cached by month
  const { cache, loading, error } = useSelector((state) => state.candidates);

  // Local UI — month picker only
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Cover letter dialog
  const [clOpen, setClOpen] = useState(false);
  const [clData, setClData] = useState(null);

  // Copy-to-clipboard snackbar
  const [copySnack, setCopySnack] = useState(false);
  const handleCopy = (e, text) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopySnack(true);
  };

  // ── Filter & sort state ───────────────────────────────────────────────────
  const [searchText, setSearchText] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterDept, setFilterDept] = useState("All");
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const monthYear = toMonthYear(selectedMonth);

  // Resolved data for the current month from Redux cache
  const cached = cache[monthYear];
  const candidates = cached?.data || [];
  const totalCount = cached?.count || 0;

  // ── Fetch logic ───────────────────────────────────────────────────────────
  // API is called only when there is no cached data for the month.
  // Refresh button always force-fetches regardless of cache.
  const loadData = useCallback(
    (date, force = false) => {
      const my = toMonthYear(date);
      dispatch(setActiveMonth(my));
      if (force || !cache[my]) {
        dispatch(fetchCandidates(my));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // First mount
  useEffect(() => {
    loadData(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Month change
  useEffect(() => {
    loadData(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const handlePrev = () => setSelectedMonth((d) => shiftMonth(d, -1));
  const handleNext = () => {
    const next = shiftMonth(selectedMonth, 1);
    if (next <= new Date()) setSelectedMonth(next);
  };
  const isNextDisabled = shiftMonth(selectedMonth, 1) > new Date();

  // Refresh button — always bypasses the cache
  const handleRefresh = () => dispatch(fetchCandidates(monthYear));

  // ── Filter & sort derived list ────────────────────────────────────────────
  const STATUS_ORDER = ["Applied", "Under Review", "Shortlisted", "Interview Scheduled", "Offer Extended", "Hired", "Rejected", "On Hold"];

  const filteredCandidates = candidates
    .filter((c) => {
      const q = searchText.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.profileId.toLowerCase().includes(q);
      const matchStatus = filterStatus === "All" || c.status === filterStatus;
      const matchDept = filterDept === "All" || c.department === filterDept;
      return matchSearch && matchStatus && matchDept;
    })
    .sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a.status || "");
      const bi = STATUS_ORDER.indexOf(b.status || "");
      const aVal = ai === -1 ? STATUS_ORDER.length : ai;
      const bVal = bi === -1 ? STATUS_ORDER.length : bi;
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });

  const allStatuses = ["All", ...STATUS_ORDER.filter((s) => candidates.some((c) => c.status === s))];
  const allDepts = ["All", ...Array.from(new Set(candidates.map((c) => c.department).filter(Boolean))).sort()];

  const activeFilterCount = (filterStatus !== "All" ? 1 : 0) + (filterDept !== "All" ? 1 : 0) + (searchText ? 1 : 0);
  const clearFilters = () => { setSearchText(""); setFilterStatus("All"); setFilterDept("All"); };

  // Derived stats
  const deptMap = candidates.reduce((acc, c) => {
    acc[c.department] = (acc[c.department] || 0) + 1;
    return acc;
  }, {});
  const topDept = Object.entries(deptMap).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  const uniqueDepts = Object.keys(deptMap).length;

  const openCoverLetter = (e, c) => {
    e.stopPropagation(); // prevent row click from firing
    setClData({ name: c.name, jobTitle: c.jobTitle, coverLetter: c.coverLetter });
    setClOpen(true);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 3 }}>
      <Container maxWidth="xl">

        {/* ── Header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>
              Hiring Dashboard
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.3 }}>
              Track all job applications month-wise
            </Typography>
          </Box>

          {/* Month navigation */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              onClick={handlePrev}
              sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
            <IconButton
              size="small"
              onClick={handleNext}
              disabled={isNextDisabled}
              sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" }, "&.Mui-disabled": { opacity: 0.35 } }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
            <Tooltip title="Force refresh" arrow>
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f1f5f9" } }}
              >
                <Refresh fontSize="small" sx={{ color: "#6366f1" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ── Stat Cards ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            {
              icon: <Groups sx={{ fontSize: 22, color: "#7c3aed" }} />,
              iconBg: "#ede9fe",
              value: loading ? "—" : totalCount,
              label: "Total Applications",
            },
            {
              icon: <CalendarToday sx={{ fontSize: 22, color: "#2563eb" }} />,
              iconBg: "#dbeafe",
              value: loading ? "—" : toMonthYear(selectedMonth),
              label: "Selected Month",
            },
            {
              icon: <Work sx={{ fontSize: 22, color: "#16a34a" }} />,
              iconBg: "#dcfce7",
              value: loading ? "—" : uniqueDepts,
              label: "Departments",
            },
            {
              icon: <Badge sx={{ fontSize: 22, color: "#d97706" }} />,
              iconBg: "#fef3c7",
              value: loading ? "—" : topDept,
              label: "Top Department",
            },
          ].map(({ icon, iconBg, value, label }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", height: "100%" }}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: "#0f172a",
                          lineHeight: 1.2,
                          fontSize: "1.35rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                        {label}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Department breakdown chips ── */}
        {!loading && Object.keys(deptMap).length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2.5}>
            {Object.entries(deptMap)
              .sort((a, b) => b[1] - a[1])
              .map(([dept, cnt]) => {
                const ds = deptStyle(dept);
                return (
                  <Chip
                    key={dept}
                    label={`${dept} · ${cnt}`}
                    size="small"
                    sx={{ bgcolor: ds.bg, color: ds.color, border: `1px solid ${ds.border}`, fontWeight: 700, fontSize: "0.75rem" }}
                  />
                );
              })}
          </Stack>
        )}

        {/* ── Candidate Table ── */}
        <Paper sx={{ borderRadius: 3, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9", bgcolor: "#fff" }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1.5} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterList sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>
                  Applicants — {monthYear}
                </Typography>
                {!loading && (
                  <Chip label={`${filteredCandidates.length} / ${totalCount}`} size="small"
                    sx={{ bgcolor: "#ede9fe", color: "#7c3aed", fontWeight: 700, fontSize: "0.72rem" }} />
                )}
              </Stack>

              {/* ── Filters row ── */}
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search name, email, ref…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 210, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
                />

                {/* Status filter */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: "0.82rem" }}>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{ borderRadius: 2, fontSize: "0.82rem" }}
                  >
                    {allStatuses.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: "0.82rem" }}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Department filter */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: "0.82rem" }}>Department</InputLabel>
                  <Select
                    value={filterDept}
                    label="Department"
                    onChange={(e) => setFilterDept(e.target.value)}
                    sx={{ borderRadius: 2, fontSize: "0.82rem" }}
                  >
                    {allDepts.map((d) => (
                      <MenuItem key={d} value={d} sx={{ fontSize: "0.82rem" }}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Clear button */}
                {activeFilterCount > 0 && (
                  <Chip
                    label={`Clear (${activeFilterCount})`}
                    size="small"
                    onDelete={clearFilters}
                    onClick={clearFilters}
                    sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 700, fontSize: "0.72rem", "& .MuiChip-deleteIcon": { color: "#dc2626" } }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress size={36} sx={{ color: "#6366f1" }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Box>
          )}

          {!loading && !error && candidates.length === 0 && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Groups sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ color: "#94a3b8", fontWeight: 600 }}>No applications for {monthYear}</Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>Try navigating to a different month</Typography>
            </Box>
          )}

          {!loading && !error && candidates.length > 0 && filteredCandidates.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <FilterList sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ color: "#94a3b8", fontWeight: 600 }}>No results match the current filters</Typography>
              <Button size="small" onClick={clearFilters} sx={{ mt: 1, textTransform: "none", color: "#6366f1", fontWeight: 700 }}>Clear filters</Button>
            </Box>
          )}

          {!loading && !error && candidates.length > 0 && filteredCandidates.length > 0 && (
            <TableContainer>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {["Ref ID", "Candidate", "Job Title", "Department", "Location", "Experience", "Mode", "Applied"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f1f5f9", py: 1.5, whiteSpace: "nowrap" }}>
                        {h}
                      </TableCell>
                    ))}
                    {/* Sortable Status column */}
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f1f5f9", py: 1.5, whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active
                        direction={sortDir}
                        onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                        sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 }, fontWeight: 700, fontSize: "0.75rem", color: "#6366f1 !important", textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f1f5f9", py: 1.5, whiteSpace: "nowrap" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCandidates.map((c, idx) => {
                    const ds = deptStyle(c.department);
                    const ms = modeStyle(c.workMode);
                    const initials = c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
                    const appliedDate = new Date(c.appliedAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });

                    return (
                      <TableRow
                        key={c.profileId}
                        onClick={() => navigate(`/candidate/${c.profileId}`, { state: { candidate: c, monthYear } })}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#eef2ff" },
                          borderBottom: idx < filteredCandidates.length - 1 ? "1px solid #f1f5f9" : "none",
                          transition: "background 0.15s",
                        }}
                      >
                        {/* Ref ID */}
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            icon={<Fingerprint sx={{ fontSize: "14px !important", color: "#6366f1 !important" }} />}
                            label={c.profileId}
                            size="small"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 800,
                              bgcolor: "#f5f3ff",
                              color: "#4f46e5",
                              border: "1px solid #c7d2fe",
                              fontSize: "0.78rem",
                              letterSpacing: 1,
                            }}
                          />
                        </TableCell>

                        {/* Candidate */}
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1.2}>
                            <Avatar
                              sx={{ width: 34, height: 34, fontSize: "0.75rem", fontWeight: 800, bgcolor: "#ede9fe", color: "#7c3aed", flexShrink: 0 }}
                            >
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem", lineHeight: 1.2 }}>
                                {c.name}
                              </Typography>
                              <Stack direction="row" spacing={0.4} alignItems="center" flexWrap="wrap">
                                <Tooltip title="Copy email" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleCopy(e, c.email)}
                                    sx={{ p: 0.3, color: "#94a3b8", "&:hover": { color: "#6366f1", bgcolor: "#f5f3ff" }, borderRadius: 1 }}
                                  >
                                    <ContentCopy sx={{ fontSize: 11 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Open Gmail compose in Firefox" arrow>
                                  <Typography
                                    component="span"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(
                                        `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(c.email)}`,
                                        "_blank",
                                        "noopener,noreferrer"
                                      );
                                    }}
                                    sx={{ fontSize: "0.7rem", color: "#94a3b8", textDecoration: "none", "&:hover": { color: "#6366f1", textDecoration: "underline" }, cursor: "pointer" }}
                                  >
                                    {c.email}
                                  </Typography>
                                </Tooltip>
                                {c.phone && (
                                  <>
                                    <Typography sx={{ fontSize: "0.7rem", color: "#cbd5e1" }}>·</Typography>
                                    <Tooltip title="Copy phone" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => handleCopy(e, c.phone)}
                                        sx={{ p: 0.3, color: "#94a3b8", "&:hover": { color: "#16a34a", bgcolor: "#f0fdf4" }, borderRadius: 1 }}
                                      >
                                        <ContentCopy sx={{ fontSize: 11 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Click to call" arrow>
                                      <Typography
                                        component="a"
                                        href={`tel:${c.phone}`}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ fontSize: "0.7rem", color: "#94a3b8", textDecoration: "none", "&:hover": { color: "#16a34a", textDecoration: "underline" }, cursor: "pointer" }}
                                      >
                                        {c.phone}
                                      </Typography>
                                    </Tooltip>
                                  </>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Job Title */}
                        <TableCell sx={{ py: 2 }}>
                          <Typography sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.83rem" }}>
                            {c.jobTitle}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                            {c.jobType} · {c.salary}
                          </Typography>
                        </TableCell>

                        {/* Department */}
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            label={c.department}
                            size="small"
                            sx={{ bgcolor: ds.bg, color: ds.color, border: `1px solid ${ds.border}`, fontWeight: 700, fontSize: "0.73rem" }}
                          />
                        </TableCell>

                        {/* Location */}
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={0.4}>
                            <LocationOn sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                              {c.location}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Experience */}
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={0.4}>
                            <WorkHistory sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                              {c.experience || "Not specified"}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Work Mode */}
                        <TableCell sx={{ py: 2 }}>
                          <Chip
                            label={c.workMode}
                            size="small"
                            sx={{ bgcolor: ms.bg, color: ms.color, fontWeight: 700, fontSize: "0.73rem" }}
                          />
                        </TableCell>

                        {/* Applied Date */}
                        <TableCell sx={{ py: 2 }}>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
                            {appliedDate}
                          </Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 2 }}>
                          {c.status ? (() => {
                            const ss = statusStyle(c.status);
                            return <Chip label={c.status} size="small" sx={{ bgcolor: ss.bg, color: ss.color, fontWeight: 700, fontSize: "0.72rem", border: `1px solid ${ss.color}25` }} />;
                          })() : (
                            <Typography sx={{ fontSize: "0.75rem", color: "#cbd5e1" }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ py: 2 }}>
                          <Stack direction="row" spacing={0.5}>
                            {c.coverLetter && (
                              <Tooltip title="View Cover Letter" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => openCoverLetter(e, c)}
                                  sx={{ color: "#6366f1", bgcolor: "#f5f3ff", "&:hover": { bgcolor: "#ede9fe" }, width: 30, height: 30 }}
                                >
                                  <Article sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {c.resumeUrl && (
                              <Tooltip title="View Resume" arrow>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={c.resumeUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ color: "#16a34a", bgcolor: "#dcfce7", "&:hover": { bgcolor: "#bbf7d0" }, width: 30, height: 30 }}
                                >
                                  <OpenInNew sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* ── Cover Letter Dialog ── */}
      <Dialog
        open={clOpen}
        onClose={() => setClOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a", pb: 0.5 }}>
          Cover Letter
          <IconButton
            size="small"
            onClick={() => setClOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12, color: "#94a3b8" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        {clData && (
          <>
            <Box sx={{ px: 3, pb: 1 }}>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                <strong>{clData.name}</strong> · {clData.jobTitle}
              </Typography>
            </Box>
            <Divider />
            <DialogContent>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: "#334155",
                  lineHeight: 1.8,
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                }}
              >
                {clData.coverLetter}
              </Typography>
            </DialogContent>
          </>
        )}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setClOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={copySnack}
        onClose={() => setCopySnack(false)}
        autoHideDuration={2000}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default Dashboard;
